/**
 * Observer
 * @module Observer
 */
export default class Observer {
	private elements: Array<Element> | Element | null
	private onIntersection: (entry: IntersectionObserverEntry) => void
	private once: boolean
	private observer: IntersectionObserver

	/**
	 * Set default variables as properties
	 * @param options Options
	 * @param options.elements HTML element/s
	 * @param options.onIntersection Callback function when the element is intersecting
	 * @param options.rootMargin Root margin to set on the IntersectionObserver
	 * @param options.threshold Threshod to set on the IntersectionObserver
	 * @param options.once Should the observer stop observing after intersection?
	 */
	constructor({
		elements = null,
		onIntersection = () => {},
		rootMargin = `0px 0px ${window.innerHeight}px 0px`,
		threshold = 0.0,
		once = true
	}: {
		elements?: Array<Element> | Element | null
		onIntersection: (entry: IntersectionObserverEntry) => void
		rootMargin?: string
		threshold?: number
		once?: boolean
	}) {
		this.elements = elements
		this.onIntersection = onIntersection
		this.once = once

		this.observer = new IntersectionObserver(this.callbackOnIntersection.bind(this), {
			rootMargin,
			threshold
		})
	}

	/**
	 * Observe elements
	 */
	observe(): void {
		if (this.elements) {
			Array.isArray(this.elements)
				? this.elements.forEach((element) => this.observer.observe(element))
				: this.observer.observe(this.elements)
		} else {
			console.warn('Error: Observer::observe: No element to observe')
		}
	}

	/**
	 * Unobserve an element
	 * @param element Element to unobserve
	 */
	unobserve(element: Element): void {
		this.observer.unobserve(element)
	}

	/**
	 * Callback function: onIntersection actions
	 * @param entries List of HTML elements being watched
	 */
	private callbackOnIntersection(entries: Array<IntersectionObserverEntry>): void {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				this.once && this.unobserve(entry.target)
				this.onIntersection(entry)
			}
		})
	}
}
